import React, { useState, useCallback } from 'react';
import {
  XMarkIcon,
  FolderIcon,
  DocumentIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
  EyeIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { useAltHotkeys } from '../hooks/useAltHotkeys';
import { useModalDrag } from '../hooks/useModalDrag';
import ModalHeader from './ui/ModalHeader';

interface FilterCondition {
  id: number;
  field: string;
  compare: string;
  values: string;
}

interface SavedFilter {
  name: string;
  conditions: FilterCondition[];
}

interface SavedFilters {
  [folderName: string]: SavedFilter[];
}

interface ModernFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (conditions: FilterCondition[], view: string) => void;
  fields: string[];
  views?: string[];
  initialConditions?: FilterCondition[];
  initialView?: string;
  savedFilters?: SavedFilters;
  onSaveFilter?: (filterName: string, conditions: FilterCondition[]) => void;
  onDeleteFilter?: (folderName: string, filterIndex: number) => void;
}

const COMPARE_OPTIONS = [
  'Equal to',
  'Not equal to',
  'Between',
  'Contains',
  'Greater than',
  'Less than',
  'Starts with',
  'Ends with',
];

export default function ModernFilterModal({
  isOpen,
  onClose,
  onSubmit,
  fields,
  views = ['Driver', 'Vehicle', 'Invoice', 'Location'],
  initialConditions,
  initialView = 'Driver',
  savedFilters = {},
  onSaveFilter,
  onDeleteFilter,
}: ModernFilterModalProps) {
  const [conditions, setConditions] = useState<FilterCondition[]>(() => {
    if (initialConditions && initialConditions.length > 0) {
      return initialConditions.map((c, i) => ({ ...c, id: i }));
    }
    return [{ id: 0, field: '', compare: '', values: '' }];
  });
  const [conditionIdCounter, setConditionIdCounter] = useState(() => {
    if (initialConditions && initialConditions.length > 0) {
      return initialConditions.length;
    }
    return 1;
  });
  const [currentView, setCurrentView] = useState(initialView);
  const [showViewDropdown, setShowViewDropdown] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [currentFolder, setCurrentFolder] = useState<string | null>(null);
  const [selectedFilterIndex, setSelectedFilterIndex] = useState<number | null>(null);

  const addCondition = useCallback(() => {
    setConditions((prev) => [
      ...prev,
      { id: conditionIdCounter, field: '', compare: '', values: '' },
    ]);
    setConditionIdCounter((prev) => prev + 1);
  }, [conditionIdCounter]);

  const removeCondition = useCallback((id: number) => {
    setConditions((prev) => {
      if (prev.length > 1) {
        return prev.filter((c) => c.id !== id);
      }
      return prev;
    });
  }, []);

  const updateCondition = useCallback(
    (id: number, field: keyof FilterCondition, value: string) => {
      setConditions((prev) =>
        prev.map((c) => (c.id === id ? { ...c, [field]: value } : c))
      );
    },
    []
  );

  const handleBlur = useCallback(
    (conditionId: number) => {
      const condition = conditions.find((c) => c.id === conditionId);
      if (!condition) return;

      const isLastCondition = conditions[conditions.length - 1].id === conditionId;
      const hasContent = condition.field || condition.compare || condition.values;

      if (isLastCondition && hasContent) {
        addCondition();
      }
    },
    [conditions, addCondition]
  );

  const resetFilters = useCallback(() => {
    setConditions([{ id: conditionIdCounter, field: '', compare: '', values: '' }]);
    setConditionIdCounter((prev) => prev + 1);
  }, [conditionIdCounter]);

  const handleSaveFilter = useCallback(() => {
    const name = prompt('Enter a name for this filter:');
    if (name && name.trim() && onSaveFilter) {
      onSaveFilter(name.trim(), conditions);
    }
  }, [conditions, onSaveFilter]);

  const handleLoadFilter = useCallback(
    (folderName: string, filterIndex: number) => {
      if (savedFilters[folderName] && savedFilters[folderName][filterIndex]) {
        const filter = savedFilters[folderName][filterIndex];
        setConditions(
          filter.conditions.map((c, i) => ({ ...c, id: conditionIdCounter + i }))
        );
        setConditionIdCounter((prev) => prev + filter.conditions.length);
        setShowLoadModal(false);
        setCurrentFolder(null);
        setSelectedFilterIndex(null);
      }
    },
    [savedFilters, conditionIdCounter]
  );

  const handleDeleteFilter = useCallback(() => {
    if (
      selectedFilterIndex !== null &&
      currentFolder &&
      savedFilters[currentFolder] &&
      onDeleteFilter
    ) {
      if (
        window.confirm(
          `Delete filter "${savedFilters[currentFolder][selectedFilterIndex].name}"?`
        )
      ) {
        onDeleteFilter(currentFolder, selectedFilterIndex);
        setSelectedFilterIndex(null);
      }
    }
  }, [selectedFilterIndex, currentFolder, savedFilters, onDeleteFilter]);

  // Alt+C closes. Applying filters isn't a save, so it gets no Alt+S — but the
  // layer keeps that chord off the grid underneath.
  useAltHotkeys({ C: onClose }, undefined, { layer: isOpen });

  const mainDrag = useModalDrag(isOpen);
  const loadDrag = useModalDrag(showLoadModal);

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-scrim bg-opacity-50 backdrop-blur-sm z-40" onClick={onClose} />

      {/* Load Filter Modal */}
      {showLoadModal && (
        <div className="fixed inset-0 bg-scrim bg-opacity-50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div
            ref={loadDrag.panelRef}
            style={loadDrag.panelStyle}
            className="bg-cmp-modal rounded-lg shadow-2xl w-full max-w-2xl max-h-[70vh] flex flex-col"
          >
            <ModalHeader
              title="Open Filter"
              onClose={() => setShowLoadModal(false)}
              dragHandleProps={loadDrag.dragHandleProps}
            />

            {/* File Browser */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="space-y-1">
                {currentFolder === null ? (
                  // Show folders
                  Object.keys(savedFilters).map((folderName) => (
                    <div
                      key={folderName}
                      onClick={() => setCurrentFolder(folderName)}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-blue-50 dark:hover:bg-gray-700 rounded cursor-pointer border border-transparent transition-colors"
                    >
                      <FolderIcon className="w-5 h-5 text-amber-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-800 dark:text-white truncate">
                          {folderName}
                        </div>
                        <div className="text-xs text-slate-500 dark:text-gray-400">
                          {savedFilters[folderName].length} filter(s)
                        </div>
                      </div>
                      <ChevronRightIcon className="w-4 h-4 text-slate-400" />
                    </div>
                  ))
                ) : (
                  <>
                    {/* Back button */}
                    <div
                      onClick={() => {
                        setCurrentFolder(null);
                        setSelectedFilterIndex(null);
                      }}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-slate-100 dark:hover:bg-gray-700 rounded cursor-pointer border border-transparent transition-colors"
                    >
                      <ArrowLeftIcon className="w-5 h-5 text-slate-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-600 dark:text-gray-400">
                          ..
                        </div>
                      </div>
                    </div>

                    {/* Files in folder */}
                    {savedFilters[currentFolder]?.map((filter, index) => (
                      <div
                        key={index}
                        onClick={() => setSelectedFilterIndex(index)}
                        onDoubleClick={() => handleLoadFilter(currentFolder, index)}
                        className={`flex items-center gap-3 px-3 py-2 hover:bg-blue-50 dark:hover:bg-gray-700 rounded cursor-pointer border transition-colors ${
                          selectedFilterIndex === index
                            ? 'bg-blue-100 dark:bg-blue-900 border-blue-400'
                            : 'border-transparent'
                        }`}
                      >
                        <DocumentIcon className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-slate-800 dark:text-white truncate">
                            {filter.name}
                          </div>
                          <div className="text-xs text-slate-500 dark:text-gray-400">
                            {filter.conditions.length} condition(s)
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>

              {Object.keys(savedFilters).length === 0 && (
                <div className="text-center py-12 text-slate-400">
                  <FolderIcon className="w-16 h-16 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">No saved filters yet</p>
                </div>
              )}
            </div>

            {/* Load Modal Footer */}
            <div className="bg-panel rounded-b-lg border-t border-slate-200 dark:border-gray-700 px-4 py-3 flex justify-between items-center">
              <button
                onClick={handleDeleteFilter}
                disabled={selectedFilterIndex === null || currentFolder === null}
                className="px-3 py-1.5 bg-red-50 text-red-600 rounded text-xs font-medium hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Delete
              </button>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowLoadModal(false);
                    setCurrentFolder(null);
                    setSelectedFilterIndex(null);
                  }}
                  className="px-4 py-2 bg-slate-100 dark:bg-gray-700 text-slate-700 dark:text-gray-300 rounded text-sm font-medium hover:bg-slate-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (selectedFilterIndex !== null && currentFolder) {
                      handleLoadFilter(currentFolder, selectedFilterIndex);
                    }
                  }}
                  disabled={selectedFilterIndex === null || currentFolder === null}
                  className="px-4 py-2 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Open
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          ref={mainDrag.panelRef}
          style={mainDrag.panelStyle}
          className="pointer-events-auto bg-panel rounded-lg shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col"
        >
          <ModalHeader
            title="Custom Filter"
            onClose={onClose}
            dragHandleProps={mainDrag.dragHandleProps}
            actions={
              <>
                {/* Reset Button */}
                <button
                  onClick={resetFilters}
                  className="text-slate-300 hover:text-white transition-colors p-1"
                  title="Reset"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                </button>

                {/* View Toggle Button */}
                <div className="relative flex items-center">
                  <button
                    onClick={() => setShowViewDropdown(!showViewDropdown)}
                    className="text-slate-300 hover:text-white transition-colors p-1"
                    title="View"
                  >
                    <EyeIcon className="w-5 h-5" />
                  </button>
                  {showViewDropdown && (
                    <div className="absolute top-full mt-1 right-0 bg-panel border border-slate-200 dark:border-gray-700 rounded-lg shadow-lg z-10 py-1 min-w-[120px]">
                      {views.map((view) => (
                        <button
                          key={view}
                          onClick={() => {
                            setCurrentView(view);
                            setShowViewDropdown(false);
                          }}
                          className="w-full text-left px-3 py-2 text-sm text-slate-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-gray-700 flex items-center justify-between"
                        >
                          <span>{view}</span>
                          {currentView === view && (
                            <CheckIcon className="w-4 h-4 text-blue-600" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Load Filter Button */}
                <button
                  onClick={() => setShowLoadModal(true)}
                  className="text-slate-300 hover:text-white transition-colors p-1"
                  title="Load"
                >
                  <FolderIcon className="w-5 h-5" />
                </button>

                {/* Save Filter Button */}
                {onSaveFilter && (
                  <button
                    onClick={handleSaveFilter}
                    className="text-slate-300 hover:text-white transition-colors p-1"
                    title="Save"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V7l-4-4z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 3v4h8V3"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 13h10v8H7z"
                      />
                    </svg>
                  </button>
                )}
              </>
            }
          />

          {/* Filter Conditions */}
          <div className="bg-panel px-4 py-3 overflow-y-auto flex-1">
            <div className="space-y-1.5">
              {conditions.map((condition) => (
                <div
                  key={condition.id}
                  className="flex gap-2 items-center bg-slate-50 dark:bg-gray-700 px-3 py-2 rounded border border-slate-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 transition-colors"
                >
                  <div className="flex-1 grid grid-cols-3 gap-2">
                    <div>
                      <select
                        value={condition.field}
                        onChange={(e) =>
                          updateCondition(condition.id, 'field', e.target.value)
                        }
                        className="w-full px-2 py-1.5 border border-slate-300 dark:border-gray-600 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-panel dark:text-white"
                      >
                        <option value="">Select field...</option>
                        {fields.map((f) => (
                          <option key={f} value={f}>
                            {f}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <select
                        value={condition.compare}
                        onChange={(e) =>
                          updateCondition(condition.id, 'compare', e.target.value)
                        }
                        className="w-full px-2 py-1.5 border border-slate-300 dark:border-gray-600 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-panel dark:text-white"
                      >
                        <option value="">Select...</option>
                        {COMPARE_OPTIONS.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <input
                        type="text"
                        value={condition.values}
                        onChange={(e) =>
                          updateCondition(condition.id, 'values', e.target.value)
                        }
                        onBlur={() => handleBlur(condition.id)}
                        placeholder={
                          condition.compare === 'Between'
                            ? '12/31/2024 to 01/31/2025'
                            : 'Enter value...'
                        }
                        className="w-full px-2 py-1.5 border border-slate-300 dark:border-gray-600 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
                      />
                    </div>
                  </div>

                  <button
                    onClick={() => removeCondition(condition.id)}
                    disabled={conditions.length === 1}
                    className={`p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900 rounded transition-colors ${
                      conditions.length === 1 ? 'opacity-30 cursor-not-allowed' : ''
                    }`}
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Footer Actions */}
          <div className="bg-panel rounded-b-lg border-t border-slate-200 dark:border-gray-700 px-4 py-3 flex justify-end gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-slate-100 dark:bg-gray-700 text-slate-700 dark:text-gray-300 rounded text-sm font-medium hover:bg-slate-200 dark:hover:bg-gray-600 transition-colors"
            >
              Close
            </button>
            <button
              onClick={() => onSubmit(conditions, currentView)}
              className="px-4 py-2 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              Submit
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
